import type {FC} from 'react';
import {useState} from 'react';
import {Theme, Roles} from '@wellstone-solutions/common';
import {
  Avatar,
  Button,
  Stack,
  Typography,
  Progress,
  Icon,
  IconNames,
  Dialog,
  DialogContent,
  DialogActions,
} from '@wellstone-solutions/web';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {DirectorySearch} from 'modules/organization';
import {useStores} from 'hooks/useStores';
import {showAlert, ALERT_TYPES} from 'utils/showAlert';
import type {ChatChannel} from '../types';

interface SelectedPerson {
  id: string;
  name: string;
  role?: string;
  user?: {id: string; email?: string};
}

interface StartConversationDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (channel: ChatChannel) => void;
}

const focusSearchInput = (node: HTMLDivElement | null) => {
  node?.querySelector('input')?.focus();
};

const SEARCHABLE_ROLES = [
  Roles.PATIENT,
  Roles.COUNSELOR,
  Roles.PEER,
  Roles.ADMIN,
  Roles.SUPER_ADMIN,
];

export const StartConversationDialog: FC<StartConversationDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const {channelStore, meStore} = useStores();
  const [selectedPerson, setSelectedPerson] = useState<SelectedPerson | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClose = () => {
    setSelectedPerson(null);
    onClose();
  };

  const handleStart = async () => {
    const userId = selectedPerson?.user?.id;
    if (!userId) {
      return;
    }

    setIsSubmitting(true);
    try {
      const channel = await channelStore.getChannelByUser(userId, true);

      if (channel) {
        channelStore.promoteChannel(channel);
        setSelectedPerson(null);
        onSubmit(channel);
      } else {
        showAlert(
          'Unable to start the conversation. Please try again.',
          ALERT_TYPES.ERROR,
        );
      }
    } catch (error) {
      showAlert(
        'Unable to start the conversation. Please try again.',
        ALERT_TYPES.ERROR,
      );
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      fullScreen={isFullScreen}
      fullWidth
      maxWidth="sm"
      open={open}
      PaperProps={{
        sx: {
          borderRadius: {
            xs: 0,
            sm: 3,
          },
          padding: 3,
        },
      }}>
      <Stack sx={{marginBottom: 3}}>
        <Avatar
          sx={{
            width: 48,
            height: 48,
            backgroundColor: 'primary4.main',
            mb: 2,
          }}>
          <Icon
            name={IconNames.ChatLinesSolid}
            size={24}
            color={Theme.colorPalette.primary}
          />
        </Avatar>

        <Typography variant="h5" sx={{fontSize: 28, fontWeight: 700}}>
          Start a new conversation
        </Typography>
        <Typography variant="body2" color="dark.main" sx={{fontSize: 16}}>
          Who would you like to chat with?
        </Typography>
      </Stack>

      <DialogContent sx={{padding: 0, overflowY: 'visible'}}>
        <div ref={focusSearchInput}>
          <DirectorySearch
            label="Search for member or staff"
            roles={SEARCHABLE_ROLES}
            excludeUserIds={[meStore.me.id]}
            onOptionSelect={(newValue: SelectedPerson | null) => {
              setSelectedPerson(newValue);
            }}
            value={selectedPerson}
            showRoleChips={true}
            testId="person-search"
          />
        </div>
        <Typography variant="body2" color="dark.main" sx={{px: 2}}>
          Enter name or email address
        </Typography>
      </DialogContent>
      <DialogActions sx={{pb: 0, pt: 3}}>
        <Button onClick={handleClose} data-testid="cancel-start-button">
          Cancel
        </Button>
        <Button
          data-testid="start-conversation-submit"
          onClick={handleStart}
          color="primary"
          variant="contained"
          disabled={isSubmitting || !selectedPerson}
          sx={{px: 3}}
          startIcon={
            isSubmitting ? (
              <Progress
                size="1.2em"
                color="inherit"
                data-testid="loading-spinner"
              />
            ) : (
              <Icon name={IconNames.ChatLinesSolid} size={24} color="inherit" />
            )
          }>
          Start
        </Button>
      </DialogActions>
    </Dialog>
  );
};
